import React, { useEffect, useRef, useState } from 'react';
import { MarketplaceCartItem } from './types';
import {
  DEFAULT_CURRENCY,
  canDecrement,
  canIncrement,
  constraintsFromItem,
  formatMoney,
  formatQuantity,
  parseQuantityInput,
  validateQuantity,
} from './cartUtils';

const API_URL = (typeof window !== 'undefined' && (window as any).API_URL) ? (window as any).API_URL : '';

const resolveImage = (url?: string) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return API_URL ? `${API_URL}${url}` : url;
};

interface ShoppingCartProps {
    items: MarketplaceCartItem[];
    isOpen: boolean;
    onClose: () => void;
    onUpdateQuantity: (listingId: number, quantity: number) => void;
    onRemoveItem: (listingId: number) => void;
    onCheckout: () => void;
    onRequestQuote?: () => void;
}

// Fallback emoji when no image is available
const getFallbackEmoji = (cropType: string) => {
    if (!cropType) return '📦';
    const t = cropType.toLowerCase();
    if (t.includes('avocado')) return '🥑';
    if (t.includes('macadamia')) return '🌰';
    if (t.includes('tomato')) return '🍅';
    if (t.includes('mango')) return '🥭';
    if (t.includes('pineapple')) return '🍍';
    if (t.includes('banana')) return '🍌';
    if (t.includes('coffee')) return '☕';
    if (t.includes('tea')) return '🍵';
    return '📦';
};

interface QuantityControlProps {
    item: MarketplaceCartItem;
    onUpdateQuantity: (listingId: number, quantity: number) => void;
}

/**
 * Editable, unit-aware quantity control.
 * +/- steppers are disabled at the min/max/stock boundaries; the numeric input
 * accepts decimal quantities for weight units and shows inline validation
 * errors (invalid, below minimum, above stock/max, wrong precision).
 */
const QuantityControl: React.FC<QuantityControlProps> = ({ item, onUpdateQuantity }) => {
    const [draft, setDraft] = useState<string>(String(item.quantity));
    const [error, setError] = useState<string | null>(null);
    const focused = useRef(false);
    const constraints = constraintsFromItem(item);

    // Keep the draft in sync when the quantity changes from elsewhere
    // (e.g. add-to-cart merging) while the user is not typing.
    useEffect(() => {
        if (!focused.current) {
            setDraft(String(item.quantity));
            setError(null);
        }
    }, [item.quantity]);

    const commit = (raw: string) => {
        focused.current = false;
        const parsed = parseQuantityInput(raw, constraints);
        if (parsed.error || !Number.isFinite(parsed.value)) {
            setError(parsed.error || 'Enter a valid quantity');
            setDraft(String(item.quantity));
            return;
        }
        const errors = validateQuantity(parsed.value, constraints);
        if (errors.length > 0) {
            setError(errors[0]);
            setDraft(String(item.quantity));
            return;
        }
        setError(null);
        if (parsed.value !== item.quantity) {
            onUpdateQuantity(item.listingId, parsed.value);
        }
    };

    const changeBy = (delta: number) => {
        const next = item.quantity + delta;
        if (delta > 0 && !canIncrement(item.quantity, constraints)) return;
        if (delta < 0 && !canDecrement(item.quantity, constraints)) return;
        setError(null);
        onUpdateQuantity(item.listingId, next);
    };

    // Step by the listing's quantityStep, not hardcoded ±1 — a 0.5 kg step
    // moves 12.5 → 13.0, never 12.5 → 13.5.
    const stepBy = (direction: 1 | -1) => changeBy(direction * (constraints.quantityStep || 1));

    return (
        <div>
            <div className="flex items-center bg-gray-50 rounded-xl h-9 border border-gray-200 overflow-hidden">
                <button
                    onClick={() => stepBy(-1)}
                    disabled={!canDecrement(item.quantity, constraints)}
                    aria-label="Decrease quantity"
                    className="w-8 h-full flex items-center justify-center text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-all font-bold disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400"
                >
                    −
                </button>
                <input
                    type="text"
                    inputMode="decimal"
                    value={draft}
                    onFocus={() => { focused.current = true; }}
                    onChange={(e) => { setDraft(e.target.value); setError(null); }}
                    onBlur={(e) => commit(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                    aria-label={`Quantity in ${item.unit}`}
                    className="w-14 text-center text-sm font-bold text-gray-900 bg-transparent outline-none focus:text-emerald-700"
                />
                <button
                    onClick={() => stepBy(1)}
                    disabled={!canIncrement(item.quantity, constraints)}
                    aria-label="Increase quantity"
                    className="w-8 h-full flex items-center justify-center text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-all font-bold disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400"
                >
                    +
                </button>
            </div>
            {error ? (
                <p role="alert" className="text-[11px] text-red-600 font-medium mt-1">{error}</p>
            ) : (
                <p className="text-[11px] text-gray-400 mt-1">
                    {item.minimumOrderQuantity > 1 ? `Min ${formatQuantity(item.minimumOrderQuantity, item.unit, item.quantityStep)}` : ''}
                    {item.maximumOrderQuantity != null ? `${item.minimumOrderQuantity > 1 ? ' • ' : ''}Max ${formatQuantity(item.maximumOrderQuantity, item.unit, item.quantityStep)}` : ''}
                </p>
            )}
        </div>
    );
};

const ShoppingCart: React.FC<ShoppingCartProps> = ({
    items, isOpen, onClose, onUpdateQuantity, onRemoveItem, onCheckout, onRequestQuote,
}) => {
    const totalValue = items.reduce((sum, item) => sum + item.subtotal, 0);
    const currencies = Array.from(new Set(items.map(item => item.currency || DEFAULT_CURRENCY)));
    const displayCurrency = currencies[0] || DEFAULT_CURRENCY;
    const hasMixedCurrencies = currencies.length > 1;

    if (!isOpen) return null;

    return (
        <div className="relative z-50">
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity" onClick={onClose} />

            {/* Cart Panel */}
            <div className="fixed inset-y-0 right-0 max-w-md w-full bg-white shadow-2xl flex flex-col">
                {/* Header */}
                <div className="px-6 py-5 border-b border-gray-100 bg-white">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                                <span className="text-xl">🛒</span>
                            </div>
                            <div>
                                <h2 className="text-xl font-extrabold text-gray-900">Your Cart</h2>
                                <p className="text-xs text-gray-500">{items.length} {items.length === 1 ? 'item' : 'items'}</p>
                            </div>
                        </div>
                        <button onClick={onClose} className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-all">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                    </div>
                </div>

                {/* Cart Items */}
                <div className="flex-1 overflow-y-auto p-6 space-y-3">
                    {items.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-center">
                            <div className="w-24 h-24 bg-gray-100 rounded-2xl flex items-center justify-center text-5xl mb-4">
                                🧺
                            </div>
                            <h3 className="text-lg font-bold text-gray-900">Your cart is empty</h3>
                            <p className="text-gray-500 text-sm mt-2 max-w-xs">Add fresh harvest products from our marketplace to get started</p>
                            <button onClick={onClose} className="mt-4 px-6 py-2.5 bg-emerald-600 text-white rounded-xl font-bold text-sm hover:bg-emerald-700 transition-all">
                                Browse Products
                            </button>
                        </div>
                    ) : (
                        items.map((item) => {
                            const displayName = item.cropType || item.catalogName || item.title || 'Product';
                            const imageSrc = resolveImage(item.imageUrl);
                            return (
                            <div key={item.listingId} className="flex gap-4 p-4 rounded-2xl border border-gray-100 bg-white hover:shadow-md transition-all group">
                                <div className="w-16 h-16 bg-gradient-to-br from-emerald-50 to-green-100 rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0 group-hover:scale-110 transition-transform">
                                    {imageSrc ? (
                                        <img src={imageSrc} alt={displayName} className="w-full h-full object-cover" />
                                    ) : (
                                        <span className="text-3xl">{getFallbackEmoji(displayName)}</span>
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <h4 className="font-bold text-gray-900">{displayName}</h4>
                                            <p className="text-xs text-gray-500">{formatMoney(item.pricePerUnit, item.currency)} / {item.unit}</p>
                                        </div>
                                        <p className="font-extrabold text-gray-900">{formatMoney(item.subtotal, item.currency)}</p>
                                    </div>

                                    <div className="flex items-center justify-between mt-3">
                                        <QuantityControl item={item} onUpdateQuantity={onUpdateQuantity} />
                                        <button
                                            onClick={() => onRemoveItem(item.listingId)}
                                            className="text-xs text-gray-400 hover:text-red-500 font-medium transition-colors flex items-center gap-1"
                                        >
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                            Remove
                                        </button>
                                    </div>
                                </div>
                            </div>
                            );
                        })
                    )}
                </div>

                {/* Footer */}
                {items.length > 0 && (
                    <div className="p-6 border-t border-gray-100 bg-white">
                        {hasMixedCurrencies && (
                            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mb-3">
                                Orders in different currencies must be placed separately.
                            </p>
                        )}
                        <div className="space-y-2 mb-5">
                            <div className="flex justify-between text-sm text-gray-600">
                                <span>Subtotal</span>
                                <span className="font-semibold">{formatMoney(totalValue, displayCurrency)}</span>
                            </div>
                            <div className="flex justify-between text-sm text-gray-600">
                                <span>Delivery</span>
                                <span className="text-gray-700 font-medium">Calculated after supplier confirmation</span>
                            </div>
                            <div className="flex justify-between text-lg font-extrabold text-gray-900 pt-3 border-t border-gray-100">
                                <span>Total</span>
                                <span>{formatMoney(totalValue, displayCurrency)}</span>
                            </div>
                        </div>
                        <div className="space-y-3">
                            <button
                                onClick={onCheckout}
                                className="w-full py-4 rounded-2xl font-extrabold text-lg bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:from-emerald-600 hover:to-green-700 shadow-xl shadow-emerald-200 hover:shadow-2xl hover:shadow-emerald-300 transition-all transform hover:-translate-y-0.5 active:scale-[0.98] flex items-center justify-center gap-2"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                                Proceed to Checkout
                            </button>
                            {onRequestQuote && (
                                <button
                                    type="button"
                                    onClick={onRequestQuote}
                                    className="w-full py-3 rounded-2xl font-semibold text-sm border-2 border-gray-200 text-gray-700 hover:border-blue-300 hover:bg-blue-50 transition-all flex items-center justify-center gap-2"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                    Request Bulk Quote
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={onClose}
                                className="w-full py-3 rounded-2xl font-semibold text-sm border-2 border-gray-200 text-gray-700 hover:border-emerald-300 hover:bg-emerald-50 transition-all flex items-center justify-center gap-2"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                                Continue Shopping
                            </button>
                        </div>
                        <p className="text-center text-[10px] text-gray-400 mt-3">Payment is arranged after the supplier confirms your order</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ShoppingCart;